////////////////////////////////////////////
// CONNECT PROVIDER — ONE CHAIN-AGNOSTIC CONNECT SURFACE
////////////////////////////////////////////
//
// THE SINGLE SOURCE OF CONNECT UX FOR THE WHOLE APP. IT NORMALIZES THE TWO
// WALLET STACKS (SOLANA wallet-adapter / RH wagmi) INTO ONE STATUS + TWO
// PER-CHAIN OPTION LISTS — THE MODAL OFFERS BOTH CHAINS' WALLETS EVERYWHERE
// (THE USER MAY CONNECT ANY APP; THE ACTIVE-WALLET MODEL FLAGS A MISMATCH
// WITH THE PAGE INSTEAD OF HIDING THE CHOICE) — AND CENTRALIZES THE
// RACE-FREE SOLANA CONNECT (select() THEN connect() ONCE THE SELECTION LANDS).
// ON MOBILE THE SOLANA OPTIONS ARE PHANTOM/SOLFLARE DEEPLINK SESSIONS
// (lib/wallet/deeplink) AND THE EVM ONES ARE PER-WALLET WALLETCONNECT
// DEEPLINKS WHOSE PAIRING IS PREPARED THE MOMENT THE MODAL OPENS, SO A ROW
// TAP NAVIGATES SYNCHRONOUSLY (ANDROID ONLY LAUNCHES APP LINKS INSIDE THE
// USER GESTURE). EVERY CONNECT TRIGGER IN THE APP CALLS openConnect().
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore,
  type ReactNode,
} from "react";
import { WalletReadyState, type WalletName } from "@solana/wallet-adapter-base";
import { useNetwork, useNetNav } from "@/config/useNetwork";
import type { NetId } from "@/config/network";
import { RH_CHAIN_ID_MAINNET, RH_CHAIN_ID_TESTNET } from "@/config/protocol";
import { useSolWallet, isMobileUA } from "@/lib/wallet/useSolWallet";
import { useEvmWallet } from "@/lib/wallet/useEvmWallet";
import { dlConnect } from "@/lib/wallet/deeplink/actions";
import { dlSession, subscribeDl, type DlProvider } from "@/lib/wallet/deeplink/store";
import { brandByDl, brandFor, DL_WALLETS, EVM_WALLETS, SOLANA_WALLETS, WC_EVM_WALLETS } from "@/lib/wallet/registry";
import { wcChoiceName, wcOpenWallet } from "@/lib/wallet/wc-deeplink";
import {
  ensureWc, retryWc as retryWcPairing, subscribeWc, wcPhaseSnapshot, wcUri, type WcPhase,
} from "@/lib/wallet/wc-pairing";
import * as log from "@/lib/log";

// MWA'S WALLET-STANDARD NAME — RELABELLED FOR A CONSUMER AUDIENCE (IT DOESN'T
// KNOW WHICH WALLET APP WILL ANSWER UNTIL ANDROID SHOWS ITS PICKER)
const MWA_NAME = "Mobile Wallet Adapter";

type WalletStatus = "disconnected" | "connecting" | "reconnecting" | "connected";


// NORMALIZED WALLET CHOICE — A SOLANA ADAPTER, A WAGMI CONNECTOR, A MOBILE
// WALLET-APP DEEPLINK SESSION, OR A CURATED INSTALL LINK
export interface ConnectOption {
  id: string;
  name: string;
  icon?: string;
  // READY TO ONE-CLICK CONNECT NOW (INSTALLED EXTENSION / IN-APP PROVIDER) VS A
  // REMOTE OPTION (WALLETCONNECT QR/RELAY, WALLET-APP DEEPLINK)
  detected: boolean;
  kind: "installed" | "walletconnect" | "deeplink" | "install";
  // BRAND ACCENT FOR OPTIONS WITHOUT AN ICON
  color?: string;
  // THIS OPTION IS THE CURRENTLY-CONNECTED WALLET (PICKING IT FIXES THE
  // NETWORK IN PLACE RATHER THAN RECONNECTING)
  connected?: boolean;
  // CURATED INSTALL LINK (kind "install" ROWS)
  install?: string;
  // PLATFORMS THE APP SERVES — RENDERED AS SUBTLE IN-ROW CHAIN BADGES
  chains?: ("solana" | "rh")[];
  // THE SOLANA MOBILE WALLET ADAPTER ENTRY (INTENT-BASED, NOT A REAL INJECTED
  // WALLET) — THE MOBILE UI HIDES IT IN FAVOUR OF THE DEEPLINK SESSIONS
  mwa?: boolean;
}

// THE CONNECTED WALLET AS THE NAV PILL SEES IT — WHICHEVER CHAIN IT LIVES
// ON. PURELY INFORMATIONAL: THE APP NEVER VALIDATES A WALLET AGAINST THE
// PAGE/TOKEN — WHATEVER IS CONNECTED IS USED, AND THE WALLET APP ITSELF
// REPORTS ANYTHING IT CAN'T SIGN.
export interface ActiveWallet {
  chain: "solana" | "rh";
  address: string;
  short: string;
  // THE WALLET APP IN USE (PHANTOM / METAMASK / …)
  app: { name: string; icon?: string; color?: string };
  // THE CHAIN THE ACCOUNT IS ACTUALLY ON — FOR EVM THIS IS DERIVED FROM THE
  // WALLET'S chainId, SO A ROBINHOOD CHAIN ACCOUNT AND AN ETHEREUM ACCOUNT
  // READ DIFFERENTLY EVEN THOUGH THEY SHARE THE SAME ADDRESS
  chainName: string;
  chainIcon: string;
  // THE NET THE ACCOUNT IS BOUND TO — INFORMATIONAL ONLY
  netLabel: string;
}

export interface ConnectContextValue {
  chain: "solana" | "rh";
  status: WalletStatus;
  connected: boolean;
  address: string | null;
  short: string | null;
  // THE CONNECTED WALLET FROM EITHER CHAIN (NAV PILL) — null WHEN NONE
  active: ActiveWallet | null;
  // ONE FLAT WALLET LIST ACROSS BOTH CHAINS (PAGE'S CHAIN LISTED FIRST,
  // SAME-BRAND ROWS MERGED) — ANY APP IS SELECTABLE REGARDLESS OF THE PAGE
  options: ConnectOption[];
  isMobile: boolean;
  // MOBILE WALLETCONNECT PAIRING STATE — wc: ROWS ARE TAPPABLE ONLY WHEN
  // "ready" (THE TAP MUST NAVIGATE SYNCHRONOUSLY WITH A MINTED URI)
  wcPairing: WcPhase;
  retryWc: () => void;
  // WHICH OPTION IS MID-CONNECT (DRIVES A PER-ROW SPINNER)
  connectingId: string | null;
  // SHARED MODAL
  isConnectOpen: boolean;
  openConnect: () => void;
  closeConnect: () => void;
  // ACTIONS
  selectOption: (id: string) => Promise<void>;
  disconnect: () => void;
}

const Ctx = createContext<ConnectContextValue | null>(null);

export function ConnectProvider({ children }: { children: ReactNode }) {

  const net = useNetwork();
  const netNav = useNetNav();
  const sol = useSolWallet();
  const evm = useEvmWallet();

  const isRh = net.isRh;
  const [isConnectOpen, setConnectOpen] = useState(false);
  const [connectingId, setConnectingId] = useState<string | null>(null);

  // PENDING SOLANA CONNECT — select() IS ASYNC STATE, SO WE FIRE connect() ONLY
  // ONCE THE SELECTION HAS LANDED (SEE THE EFFECT). AVOIDS THE STALE-ADAPTER RACE
  // THE OLD INLINE `select(); connect()` CALL SITES LEANED ON autoConnect TO PAPER OVER.
  const pendingSol = useRef<string | null>(null);

  // CROSS-CHAIN PICK — WHEN THE USER EXPLICITLY PICKS A WALLET FROM THE OTHER
  // CHAIN'S STACK (PHANTOM ON AN RH PAGE), FLIP ?net= TO THAT CHAIN ON SUCCESS
  // INSTEAD OF LANDING THEM ON A RED PILL. SET ONLY BY A PICKER CLICK — A
  // RESTORED/AUTO-CONNECTED SESSION MUST NEVER REWRITE A URL THE USER OPENED.
  // TEST TIER IS PRESERVED (rh-testnet → devnet PAIR VIA net.net / net.rhNet).
  const flipNetTo = useRef<NetId | null>(null);

  const openConnect = useCallback(() => setConnectOpen(true), []);
  const closeConnect = useCallback(() => {

    setConnectOpen(false);
    // DROP ANY STALE "CONNECTING" SO A REOPEN ISN'T STUCK WITH A SPINNER +
    // DISABLED ROWS, AND DROP A NOT-YET-FIRED SOLANA CONNECT SO A WALLET SHEET
    // CAN'T POP AFTER THE USER DISMISSED THE MODAL (AN ALREADY-IN-FLIGHT CONNECT
    // STILL RESOLVES VIA THE STATE EFFECTS).
    setConnectingId(null);
    pendingSol.current = null;
    flipNetTo.current = null;
  }, []);

  // ENVIRONMENT — FIXED FOR THE TAB
  const isMobile = useMemo(() => isMobileUA(), []);

  // DEEPLINK SESSION — READ RAW (ANY NET) SO A CLUSTER-MISMATCHED SESSION
  // STILL SURFACES AS A RED PILL INSTEAD OF SILENTLY READING AS DISCONNECTED
  const rawDl = useSyncExternalStore(subscribeDl, dlSession);

  // CURATED BRANDS NOT ALREADY OFFERED — RENDERED AS "GET A WALLET" LINKS
  const installsFor = useCallback((brands: typeof EVM_WALLETS, offered: ConnectOption[]): ConnectOption[] =>
    brands
      .filter((w) => !offered.some((o) => o.id === "dl:" + w.dl || brandFor(o.name)?.id === w.id))
      .map((w) => ({
        id: "install:" + w.id, name: w.name, icon: w.icon, color: w.color,
        detected: false, kind: "install" as const, install: w.installUrl,
      })), []);

  // SOLANA CHOICES — DISCOVERED/DEEPLINK WALLETS FIRST, THEN THE REST OF THE
  // CURATED REGISTRY AS INSTALL LINKS (lib/wallet/registry). MOBILE: INSIDE A
  // WALLET'S IN-APP BROWSER THE INJECTED PROVIDER WINS (NO INSTALL NOISE); A
  // BARE NATIVE BROWSER GETS THE WALLET-APP DEEPLINK SESSIONS.
  const solOptions: ConnectOption[] = useMemo(() => {

    const base: ConnectOption[] = sol.wallets.map((w) => ({
      id: w.adapter.name,
      name: w.adapter.name === MWA_NAME ? "Installed wallet app" : w.adapter.name,
      icon: w.adapter.icon,
      detected: w.readyState === WalletReadyState.Installed || w.readyState === WalletReadyState.Loadable,
      kind: "installed" as const,
      connected: sol.connected && sol.wallet?.adapter.name === w.adapter.name,
      mwa: w.adapter.name === MWA_NAME,
    }));
    if (isMobile) {

      if (base.some((o) => o.detected && !o.mwa))
        return base;
      const dl: ConnectOption[] = DL_WALLETS.map((w) => ({
        id: "dl:" + w.dl,
        name: w.name,
        icon: w.icon,
        color: w.color,
        detected: true,
        kind: "deeplink" as const,
        connected: rawDl?.provider === w.dl,
      }));
      return [...dl, ...installsFor(SOLANA_WALLETS, dl)];
    }
    return [...base, ...installsFor(SOLANA_WALLETS, base)];
  }, [sol.wallets, sol.connected, sol.wallet, isMobile, rawDl, installsFor]);

  // EVM CHOICES — METAMASK RIDES ITS OFFICIAL SDK CONNECTOR (EXTENSION ON
  // DESKTOP, OWN-TRANSPORT DEEPLINK ON MOBILE); RAINBOW/TRUST DEEPLINK OVER
  // THE PREPARED WALLETCONNECT PAIRING (NOTHING CAN INJECT INTO A PHONE
  // BROWSER). INSIDE A WALLET'S IN-APP BROWSER THE INJECTED PROVIDER WINS.
  const evmOptions: ConnectOption[] = useMemo(() => {

    const mmInjected = typeof window !== "undefined" &&
      !!(window as { ethereum?: { isMetaMask?: boolean } }).ethereum?.isMetaMask;

    // A DISCOVERED EVM PROVIDER WHOSE BRAND THE REGISTRY MARKS SOLANA-ONLY
    // (PHANTOM/BACKPACK — FIXED EVM NETWORK LISTS, CAN NEVER ADD ROBINHOOD
    // CHAIN) MUST NOT BECOME A CONNECT PATH: ON AN RH PAGE THE MERGE WOULD
    // PUT ITS EVM ROW FIRST AND A CLICK WOULD ASK THE WALLET FOR ETHEREUM
    // ("Unsupported account" ON A SOLANA-ONLY ACCOUNT). UNKNOWN BRANDS PASS.
    const discovered: ConnectOption[] = evm.connectors
      .filter((c) => brandFor(c.name)?.chains.includes("evm") ?? true)
      .map((c) => ({
      id: c.uid,
      name: c.name,
      icon: c.icon ?? brandFor(c.name)?.icon,
      color: brandFor(c.name)?.color,
      detected: c.type === "walletConnect" ? false : c.type === "metaMask" ? mmInjected : true,
      kind: c.type === "walletConnect" ? "walletconnect" as const
        : c.type === "metaMask" && isMobile && !mmInjected ? "deeplink" as const
        : "installed" as const,
      connected: evm.connected && c.uid === evm.connectorUid,
    }));
    if (isMobile) {

      const injected = discovered.filter((o) => o.kind === "installed" && o.detected);
      if (injected.length)
        return injected;
      // THE SDK ROW READS AS A WALLET APP — TAPPABLE EVEN THOUGH NOTHING IS
      // INJECTED (THE SDK BRIDGES TO THE APP ITSELF)
      const sdkRows = discovered
        .filter((o) => o.kind === "deeplink")
        .map((o) => ({ ...o, detected: true }));
      const wcConnected = evm.connected &&
        evm.connectors.find((x) => x.uid === evm.connectorUid)?.type === "walletConnect";
      const choice = wcChoiceName();
      const wcRows: ConnectOption[] = WC_EVM_WALLETS.map((w) => ({
        id: "wc:" + w.id,
        name: w.name,
        icon: w.icon,
        color: w.color,
        detected: true,
        kind: "deeplink" as const,
        connected: wcConnected && choice === w.name,
      }));
      const offered = [...sdkRows, ...wcRows];
      return [...offered, ...installsFor(EVM_WALLETS, offered)];
    }
    return [...discovered, ...installsFor(EVM_WALLETS, discovered)];
  }, [evm.connectors, evm.connected, evm.connectorUid, isMobile, installsFor]);

  ////////////////////////////////////////////
  // ONE FLAT LIST — BOTH CHAINS MERGED, PAGE'S CHAIN FIRST. A BRAND OFFERED
  // BY BOTH STACKS (E.G. METAMASK EXTENSION = WALLET-STANDARD + EIP-6963)
  // COLLAPSES TO ONE ROW WHOSE CLICK TAKES THE PAGE-CHAIN PATH; ITS BADGES
  // SHOW EVERY PLATFORM THE APP SERVES (REGISTRY chains). INSTALL LINKS ARE
  // UNIONED AND DROPPED WHEN THE BRAND ALREADY HAS A CONNECT ROW.
  ////////////////////////////////////////////
  const options: ConnectOption[] = useMemo(() => {

    const keyOf = (o: ConnectOption) => brandFor(o.name)?.id ?? "raw:" + o.name.toLowerCase();
    const badges = (o: ConnectOption, source: "solana" | "rh"): ("solana" | "rh")[] => {

      const known = brandFor(o.name)?.chains.map((ch) => (ch === "evm" ? "rh" as const : "solana" as const));
      return known ?? [source];
    };

    const ordered: { list: ConnectOption[]; source: "solana" | "rh" }[] = isRh
      ? [{ list: evmOptions, source: "rh" }, { list: solOptions, source: "solana" }]
      : [{ list: solOptions, source: "solana" }, { list: evmOptions, source: "rh" }];

    const choices: ConnectOption[] = [];
    const installs: ConnectOption[] = [];
    for (const { list, source } of ordered) {

      for (const o of list) {

        if (o.kind === "install") {

          if (!installs.some((x) => keyOf(x) === keyOf(o)))
            installs.push({ ...o, chains: badges(o, source) });
          continue;
        }
        const dup = choices.find((x) => keyOf(x) === keyOf(o));
        if (dup) {

          dup.chains = [...new Set([...(dup.chains ?? []), ...badges(o, source)])];
          continue;
        }
        choices.push({ ...o, chains: badges(o, source) });
      }
    }
    return [...choices, ...installs.filter((i) => !choices.some((x) => keyOf(x) === keyOf(i)))];
  }, [isRh, solOptions, evmOptions]);

  // STATUS — NORMALIZED OFF EACH STACK'S OWN LIFECYCLE. WHILE THE PAGE'S OWN
  // CHAIN IS BARE, A RESTORING OTHER-CHAIN SESSION STILL READS AS PENDING SO
  // THE NAV SHOWS "Connecting…" INSTEAD OF FLICKERING "Connect wallet" → RED.
  const pageStatus: WalletStatus = isRh
    ? (evm.status as WalletStatus)
    : sol.connected ? "connected" : sol.connecting ? "connecting" : "disconnected";
  const otherPending = isRh
    ? sol.connecting
    : evm.status === "connecting" || evm.status === "reconnecting";
  const status: WalletStatus = pageStatus === "disconnected" && otherPending ? "reconnecting" : pageStatus;

  const connected = isRh ? evm.connected : sol.connected;
  const address = isRh ? evm.address : sol.pubkey;
  const short = isRh ? evm.short : sol.short;

  ////////////////////////////////////////////
  // ACTIVE WALLET — WHAT THE NAV PILL SHOWS (INFORMATIONAL ONLY). THE PAGE'S
  // OWN CHAIN WINS WHEN BOTH ARE CONNECTED; NOTHING IS VALIDATED AGAINST THE
  // PAGE — WHATEVER THE USER CONNECTED IS THE WALLET.
  ////////////////////////////////////////////
  const active: ActiveWallet | null = useMemo(() => {

    // SOLANA — ADAPTER OR DEEPLINK SESSION (EITHER SETS sol.pubkey)
    let solD: ActiveWallet | null = null;
    if (sol.pubkey) {

      const dlBrand = sol.dlActive && rawDl ? brandByDl(rawDl.provider) : null;
      solD = {
        chain: "solana",
        address: sol.pubkey,
        short: sol.short!,
        app: dlBrand
          ? { name: dlBrand.name, icon: dlBrand.icon, color: dlBrand.color }
          : { name: sol.wallet?.adapter.name ?? "Solana wallet", icon: sol.wallet?.adapter.icon },
        chainName: "Solana",
        chainIcon: "/solana.svg",
        // DEEPLINK SESSIONS KNOW THEIR CLUSTER; ADAPTERS FOLLOW THE PAGE
        netLabel: dlBrand && rawDl
          ? (rawDl.net === "devnet" ? "devnet" : "mainnet-beta")
          : (net.isDevnet && !isRh ? "devnet" : "mainnet-beta"),
      };
    }

    // EVM — LABEL THE CHAIN THE ACCOUNT IS ACTUALLY ON
    let evmD: ActiveWallet | null = null;
    if (evm.connected && evm.address) {

      const c = evm.connectors.find((x) => x.uid === evm.connectorUid);
      // A WALLETCONNECT SESSION'S APP IS THE ONE IT WAS PAIRED WITH — THE
      // DEEPLINK CHOICE NAMES IT (THE CONNECTOR ITSELF IS JUST "WalletConnect")
      const brand = c?.type === "walletConnect" ? brandFor(wcChoiceName()) ?? brandFor(c?.name) : brandFor(c?.name);
      // WHICH EVM CHAIN IS THIS ACCOUNT ON — chainId IS THE ONLY THING THAT
      // DISTINGUISHES A ROBINHOOD CHAIN ACCOUNT FROM AN ETHEREUM ONE (SAME
      // ADDRESS SPACE); UNKNOWN CHAINS READ AS ETHEREUM WITH THEIR RAW ID
      const onRh = evm.chainId === RH_CHAIN_ID_MAINNET || evm.chainId === RH_CHAIN_ID_TESTNET;
      evmD = {
        chain: "rh",
        address: evm.address,
        short: evm.short!,
        app: {
          name: (c?.type === "walletConnect" && brand ? brand.name : c?.name) ?? "Wallet",
          icon: c?.type === "walletConnect" ? brand?.icon : (c?.icon ?? brand?.icon),
          color: brand?.color,
        },
        chainName: onRh ? "Robinhood Chain" : "Ethereum",
        chainIcon: onRh ? "/robinhood-icon.png" : "/ethereum.svg",
        netLabel: evm.chainId === RH_CHAIN_ID_MAINNET ? "mainnet"
          : evm.chainId === RH_CHAIN_ID_TESTNET ? "testnet"
          : evm.chainId === 1 ? "mainnet"
          : "chain " + evm.chainId,
      };
    }

    // THE PAGE'S CHAIN FIRST — ITS WALLET OUTRANKS THE OTHER CHAIN'S
    return isRh ? (evmD ?? solD) : (solD ?? evmD);
  }, [isRh, net.isDevnet, sol.pubkey, sol.short, sol.wallet, sol.dlActive, rawDl, evm.connected, evm.address, evm.short, evm.chainId, evm.connectors, evm.connectorUid]);

  ////////////////////////////////////////////
  // MOBILE WALLETCONNECT PRE-PAIRING — DELEGATED TO THE GLOBAL MANAGER
  // (lib/wallet/wc-pairing). THE URI TAKES 1-2s TO MINT; NAVIGATING THAT
  // LATE FALLS OUTSIDE THE TAP GESTURE AND ANDROID LOADS THE WALLET'S
  // WEBSITE INSTEAD OF LAUNCHING THE APP, SO PAIRING STARTS ON MODAL OPEN
  // AND ROW TAPS NAVIGATE SYNCHRONOUSLY. THE MANAGER IS A SINGLETON ON
  // PURPOSE: THIS PROVIDER REMOUNTS PER ROUTE, AND PER-MOUNT STATE CAUSED
  // CONCURRENT connect() CALLS + TRANSPORT-CLOSING ABORTS ("Connection
  // interrupted while trying to subscribe"). AN UNUSED PAIRING IS LEFT TO
  // EXPIRE — NEVER ABORTED ON MODAL CLOSE.
  ////////////////////////////////////////////
  const wcPairing = useSyncExternalStore(subscribeWc, wcPhaseSnapshot);

  useEffect(() => {

    // AUTOMATED RUNS (PLAYWRIGHT MOCKS ABORT THE RELAY) — STAY idle SO THE
    // ROWS RENDER THEIR PLAIN STATE AND SNAPSHOTS ARE DETERMINISTIC
    if (!isConnectOpen || !isMobile || evm.connected || navigator.webdriver)
      return;

    const wc = evm.connectors.find((c) => c.type === "walletConnect");
    if (wc)
      ensureWc(wc, evm.connectAsync);
  }, [isConnectOpen, isMobile, evm.connected, evm.connectors, evm.connectAsync]);

  const retryWc = useCallback(() => {

    const wc = evm.connectors.find((c) => c.type === "walletConnect");
    // NO LIVE SESSION → ALSO WIPE THE wc@2 CACHE (POISONED SUBSCRIPTION
    // RECORDS SURFACE AS "Subscribing to <topic> failed" ON EVERY ATTEMPT)
    if (wc)
      retryWcPairing(wc, evm.connectAsync, { clearStorage: !evm.connected });
  }, [evm.connectors, evm.connectAsync, evm.connected]);

  // FIRE THE SOLANA CONNECT — SHARED BY selectOption (WALLET ALREADY SELECTED)
  // AND THE SELECTION-LANDED EFFECT (FRESH SELECTION). CLEARS PENDING SO IT
  // CAN'T RE-FIRE, AND ALWAYS CLEARS THE SPINNER WHEN IT SETTLES.
  const runSolConnect = useCallback(() => {

    pendingSol.current = null;
    sol.connect()
      .catch((e) => classifyCancel("wallet", e))
      .finally(() => setConnectingId(null));
  }, [sol]);

  // CONNECT A SPECIFIC OPTION
  const selectOption = useCallback(async (id: string) => {

    // WALLET-APP DEEPLINK SESSION — NAVIGATES TO THE WALLET'S UNIVERSAL LINK;
    // THE APPROVED SESSION COMES BACK ON THE REDIRECTED LOAD (deeplink/resume).
    // onCancel UNSTICKS THE SPINNER IF THE USER SWITCHES BACK WITHOUT APPROVING.
    if (id.startsWith("dl:")) {

      flipNetTo.current = net.isRh ? net.net : null;
      setConnectingId(id);
      dlConnect(id.slice(3) as DlProvider, net.net, { onCancel: () => setConnectingId(null) });
      return;
    }

    // CURATED EVM WALLET APP (MOBILE) — DEEPLINK INTO THE CHOSEN APP WITH THE
    // PRE-PAIRED URI, SYNCHRONOUSLY (INSIDE THE TAP GESTURE, SO ANDROID
    // ACTUALLY LAUNCHES THE APP). PICKING THE APP THIS SESSION IS ALREADY
    // PAIRED WITH JUST FIXES THE CHAIN. NO URI = THE ROW WAS RENDERED
    // preparing/failed (UNTAPPABLE) — NEVER START A SECOND connectAsync HERE,
    // THE PREPARED ONE IS ALREADY IN FLIGHT AND A DUPLICATE JUST ERRORS.
    if (id.startsWith("wc:")) {

      const brand = WC_EVM_WALLETS.find((w) => w.id === id.slice(3));
      const wc = evm.connectors.find((x) => x.type === "walletConnect");
      if (!brand || !wc) return;

      const pairedHere = evm.connected && evm.connectorUid === wc.uid && wcChoiceName() === brand.name;
      if (pairedHere) {

        try { await evm.ensureChain(); } catch (e) { classifyCancel("wallet-evm", e); }
        if (!net.isRh) netNav(net.rhNet, true);
        setConnectOpen(false);
        return;
      }
      const uri = wcUri();
      if (!uri)
        return;

      flipNetTo.current = !net.isRh ? net.rhNet : null;
      setConnectingId(id);
      wcOpenWallet(brand, uri);
      return;
    }

    // WAGMI CONNECTOR — RESOLVED BY uid, WHATEVER CHAIN THE PAGE IS ON (THE
    // MODAL OFFERS BOTH CHAINS' WALLETS EVERYWHERE)
    const c = evm.connectors.find((x) => x.uid === id);
    if (c) {

      flipNetTo.current = !net.isRh ? net.rhNet : null;
      setConnectingId(id);
      try {
        // PICKING THE ALREADY-CONNECTED WALLET — THE ONLY THING TO FIX IS THE
        // CHAIN (WRONG-NETWORK RECOVERY); RECONNECTING WOULD THROW IN WAGMI
        if (evm.connected && c.uid === evm.connectorUid) {

          await evm.ensureChain();
          if (!net.isRh) netNav(net.rhNet, true);
          setConnectOpen(false);
        } else {

          // SWITCHING WALLETS — RELEASE THE OLD CONNECTION FIRST; WAGMI KEEPS
          // A CONNECTIONS MAP, SO A LEFTOVER LIVE CONNECTOR WOULD SILENTLY
          // RESURFACE AS "CONNECTED" AFTER A LATER DISCONNECT
          if (evm.connected) await evm.disconnectAsync();
          await evm.connectAsync({ connector: c });
        }
      } catch (e) {
        classifyCancel("wallet-evm", e);
      } finally {
        setConnectingId(null);
      }
      return;
    }
    // SOLANA — ALREADY CONNECTED TO THIS WALLET: NOTHING TO FIX (BUT AN RH
    // PAGE STILL FLIPS — CLICKING YOUR SOLANA WALLET MEANS "USE THIS"), JUST
    // CLOSE. ALREADY SELECTED BUT DISCONNECTED: select() IS A NO-OP (STATE
    // DOESN'T CHANGE, SO THE EFFECT NEVER RUNS) — DRIVE connect() DIRECTLY.
    // OTHERWISE SELECT AND LET THE EFFECT CONNECT ONCE THE SELECTION LANDS.
    if (sol.connected && sol.wallet?.adapter.name === id) {

      if (net.isRh) netNav(net.net, true);
      setConnectOpen(false);
      return;
    }
    flipNetTo.current = net.isRh ? net.net : null;
    setConnectingId(id);
    if (sol.wallet?.adapter.name === id) {

      if (!sol.connecting) runSolConnect();
      return;
    }
    pendingSol.current = id;
    sol.select(id as WalletName);
  }, [evm, sol, runSolConnect, net.net, net.isRh, net.rhNet, netNav]);

  // SOLANA — FINISH THE CONNECT ONCE THE (FRESHLY) SELECTED WALLET HAS LANDED
  useEffect(() => {

    const want = pendingSol.current;
    if (!want) return;

    // AUTO-CONNECT (USER-SELECTED) MAY ALREADY BE CONNECTING — DON'T DOUBLE-FIRE
    if (sol.connecting) return;

    if (sol.wallet?.adapter.name === want && !sol.connected)
      runSolConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sol.wallet, sol.connected, sol.connecting]);

  // CLOSE THE MODAL + CLEAR PENDING WHEN A WALLET LANDS — PER-CHAIN RISING
  // EDGES, NOT "ANY CONNECTED": WITH THE OTHER CHAIN ALREADY (OR STALELY —
  // WAGMI'S CONNECTIONS MAP CAN RESURFACE) CONNECTED, AN any-CONNECTED FLAG
  // IS ALREADY HIGH, SO A FRESH CONNECT PRODUCED NO EDGE AND THE MODAL SAT
  // OPEN LOOKING DEAD. RISING-EDGE-ONLY STILL HOLDS PER CHAIN, SO A RED-PILL
  // VISIT (OTHER CHAIN ALREADY CONNECTED) DOESN'T INSTANTLY DISMISS THE PICKER
  const prevSolConnected = useRef(sol.connected);
  const prevEvmConnected = useRef(evm.connected);
  useEffect(() => {

    const solRose = sol.connected && !prevSolConnected.current;
    const evmRose = evm.connected && !prevEvmConnected.current;
    prevSolConnected.current = sol.connected;
    prevEvmConnected.current = evm.connected;
    if (solRose || evmRose) {

      // PICKER-INITIATED CROSS-CHAIN CONNECT LANDED — FOLLOW THE WALLET'S
      // CHAIN (PLATFORM SWITCH, SO ?launch DROPS — A KEY IS CHAIN-BOUND).
      // ONLY WHEN THE RISING CHAIN IS THE ONE THE PICK TARGETED — AN
      // UNRELATED RISE ON THE OTHER CHAIN MUST NOT HIJACK THE URL.
      const flip = flipNetTo.current;
      const flipIsRh = flip === "rh" || flip === "rh-testnet";
      if (flip && (flipIsRh ? evmRose : solRose)) {

        netNav(flip, true);
        flipNetTo.current = null;
      }
      setConnectOpen(false);
      setConnectingId(null);
      pendingSol.current = null;
    }
  }, [sol.connected, evm.connected, netNav]);

  // SAFETY NET — WHEN A SOLANA CONNECT ATTEMPT SETTLES WITHOUT CONNECTING
  // (E.G. autoConnect DROVE IT AND THE USER CANCELLED, SO OUR OWN connect()'s
  // finally NEVER RAN), CLEAR THE PENDING SPINNER SO THE MODAL ISN'T STUCK.
  const wasConnectingSol = useRef(false);
  useEffect(() => {

    if (sol.connecting) {

      wasConnectingSol.current = true;
      return;
    }
    if (wasConnectingSol.current) {

      wasConnectingSol.current = false;
      pendingSol.current = null;
      setConnectingId(null);
    }
  }, [sol.connecting]);

  // DISCONNECT THE WALLET THE PILL SHOWS — NOT THE PAGE'S CHAIN (A MISMATCHED
  // OTHER-CHAIN WALLET MUST BE DISCONNECTABLE TOO)
  const disconnect = useCallback(() => {

    if (active?.chain === "rh") evm.disconnect();
    else sol.disconnect().catch(() => { /* IGNORE */ });
  }, [active, evm, sol]);

  const value: ConnectContextValue = useMemo(() => ({
    chain: isRh ? "rh" : "solana",
    status, connected, address, short, active, options,
    isMobile, wcPairing, retryWc, connectingId,
    isConnectOpen, openConnect, closeConnect,
    selectOption, disconnect,
  }), [
    isRh, status, connected, address, short, active, options, isMobile,
    wcPairing, retryWc, connectingId, isConnectOpen, openConnect, closeConnect,
    selectOption, disconnect,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useConnect(): ConnectContextValue {

  const c = useContext(Ctx);
  if (!c) throw new Error("useConnect outside ConnectProvider");
  return c;
}

// CANCEL CLASSIFIER — A USER DISMISS IS EXPECTED, NOT AN ERROR (SAME CODES THE
// PER-CHAIN sign PATHS ALREADY CLASSIFY: EIP-1193 4001 / ACTION_REJECTED / MWA)
function classifyCancel(cat: string, e: unknown) {

  const err = e as { code?: number | string; message?: string; error?: { code?: number } };
  const code = err?.code ?? err?.error?.code;
  const cancelled = code === 4001 || code === "ACTION_REJECTED" || /reject|cancel|declin|denied|user/i.test(err?.message || "");
  if (cancelled) log.info(cat, "connect cancelled", { code });
  else log.warn(cat, "connect failed", { err: err?.message, code });
}
